import type { AgentWorkflowEvent } from '@/services/agents/run-events-stream';

export const MAX_EVENTS_PER_GROUP = 50;

export type AgentEventItemKind =
  | 'thinking'
  | 'message'
  | 'tool_call'
  | 'tool_result'
  | 'error';

export interface AgentEventItem {
  key: string;
  kind: AgentEventItemKind;
  text: string;
  toolName?: string;
  detail?: string;
  blockId?: string;
  /** ISO timestamp of the item's first frame. */
  at?: string;
  /**
   * True when the source frame was a textstream delta (concatenated into
   * the open stream); false/absent for finalised blocks, which supersede
   * prior deltas sharing the same block_id.
   */
  delta?: boolean;
}

export interface AgentEventGroup {
  key: string;
  displayName: string;
  items: AgentEventItem[];
  errorCount: number;
  toolCallCount: number;
}

const FALLBACK_GROUP_NAME = 'Workflow';

function groupNameFor(event: AgentWorkflowEvent): string {
  return (
    event.node?.display_name || event.node?.node_type || FALLBACK_GROUP_NAME
  );
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function extraOf(data: Record<string, unknown>): Record<string, unknown> {
  const extra = data.extra;
  return extra && typeof extra === 'object'
    ? (extra as Record<string, unknown>)
    : {};
}

/**
 * Payload slot of events promoted from legacy storage types: the legacy
 * envelope nests its fields under ``data`` (e.g. ``extra.data.tool_name``
 * for tool frames). For thinking/message frames the slot is a plain
 * string instead, which callers read via ``asString(extraOf(data).data)``.
 */
function legacyDataOf(data: Record<string, unknown>): Record<string, unknown> {
  const nested = extraOf(data).data;
  return nested && typeof nested === 'object' && !Array.isArray(nested)
    ? (nested as Record<string, unknown>)
    : {};
}

/**
 * Reads a canonical wire field with fallbacks into the adapter's
 * forward-compat `extra` slot and the legacy envelope's nested payload:
 * events promoted from legacy storage types carry their fields under
 * `data.extra` or `data.extra.data` instead of the canonical top-level
 * keys.
 */
function field(data: Record<string, unknown>, name: string): string {
  return (
    asString(data[name]) ||
    asString(extraOf(data)[name]) ||
    asString(legacyDataOf(data)[name])
  );
}

/**
 * True when the frame is a textstream delta. The gateway's event adapter
 * stamps a top-level ``mode: "block"`` default without promoting the
 * payload's real mode out of ``data.extra``, so the top-level value alone
 * is unreliable: live deltas arrive as ``{mode: "block", extra: {mode:
 * "textstream", block_id: ...}}``. Treat the frame as a delta when either
 * location says textstream.
 */
function isTextstreamFrame(data: Record<string, unknown>): boolean {
  return (
    asString(data.mode) === 'textstream' ||
    asString(extraOf(data).mode) === 'textstream'
  );
}

function userFacingErrorText(data: Record<string, unknown>): string {
  const direct =
    asString(data.message) ||
    asString(data.error_message) ||
    asString(data.error) ||
    asString(data.text);
  if (direct) return direct;
  const extra = data.extra;
  if (extra && typeof extra === 'object') {
    const record = extra as Record<string, unknown>;
    return (
      asString(record.message) ||
      asString(record.error_message) ||
      asString(record.error) ||
      asString(record.text)
    );
  }
  return '';
}

function itemFor(
  event: AgentWorkflowEvent,
  index: number,
): AgentEventItem | null {
  const data = event.data ?? {};
  const key = event.event_id || `evt-${index}`;
  switch (event.event_type) {
    case 'node.thinking': {
      const text = field(data, 'thought') || asString(extraOf(data).data);
      if (!text) return null;
      return {
        key,
        kind: 'thinking',
        text,
        at: event.timestamp,
        blockId: field(data, 'block_id') || undefined,
        delta: isTextstreamFrame(data),
      };
    }
    case 'node.agent_message': {
      const text = field(data, 'content') || asString(extraOf(data).data);
      if (!text) return null;
      return {
        key,
        kind: 'message',
        text,
        at: event.timestamp,
        blockId: field(data, 'block_id') || undefined,
        delta: isTextstreamFrame(data),
      };
    }
    case 'node.tool_call':
      return {
        key,
        kind: 'tool_call',
        text: '',
        at: event.timestamp,
        toolName: field(data, 'tool_name') || 'tool',
        detail: field(data, 'tool_arguments'),
      };
    case 'node.tool_result':
      return {
        key,
        kind: 'tool_result',
        text: '',
        at: event.timestamp,
        toolName: field(data, 'tool_name') || 'tool',
        detail: field(data, 'tool_result'),
      };
    case 'node.node_failed': {
      const hint = field(data, 'actionable_hint');
      return {
        key,
        kind: 'error',
        text: field(data, 'error_message') || 'Node failed',
        at: event.timestamp,
        detail: hint || undefined,
      };
    }
    case 'unknown': {
      if (event.legacy_type !== 'user_facing_error') return null;
      const text = userFacingErrorText(data);
      if (!text) return null;
      return { key, kind: 'error', text, at: event.timestamp };
    }
    default:
      return null;
  }
}

/**
 * Groups raw workflow events into per-agent progress sections keyed by
 * the emitting node's display_name. Textstream deltas (thinking /
 * agent_message frames sharing a block_id) are concatenated into one
 * item even across interleaved tool events, and a finalised
 * ``mode: "block"`` frame replaces the deltas it supersedes; each group
 * retains at most MAX_EVENTS_PER_GROUP items.
 */
export function groupAgentEvents(
  events: AgentWorkflowEvent[],
): AgentEventGroup[] {
  const groups = new Map<string, AgentEventGroup>();
  /** Open text streams keyed by group + kind + block_id. */
  const streams = new Map<string, AgentEventItem>();

  events.forEach((event, index) => {
    const item = itemFor(event, index);
    if (!item) return;

    const name = groupNameFor(event);
    let group = groups.get(name);
    if (!group) {
      group = {
        key: name,
        displayName: name,
        items: [],
        errorCount: 0,
        toolCallCount: 0,
      };
      groups.set(name, group);
    }

    if (item.kind === 'thinking' || item.kind === 'message') {
      const last = group.items[group.items.length - 1];
      // Deltas without a block_id can only merge into an adjacent open
      // anonymous stream of the same kind; keyed streams merge across
      // interleaved tool events too.
      const streamKey = item.blockId
        ? `${name}\u0000${item.kind}:${item.blockId}`
        : undefined;
      const open = streamKey
        ? streams.get(streamKey)
        : item.delta && last?.kind === item.kind && last.delta && !last.blockId
          ? last
          : undefined;
      // The includes() guard drops streams whose item was spliced out by
      // the per-group cap; a new item is started instead.
      if (open && group.items.includes(open)) {
        if (item.delta) {
          open.text += item.text;
        } else {
          // Finalised block supersedes the accumulated deltas (wire 1.0.2).
          open.text = item.text;
          open.delta = false;
        }
        if (!open.at) open.at = item.at;
        return;
      }
      if (streamKey) streams.set(streamKey, item);
    }

    if (item.kind === 'error') group.errorCount += 1;
    if (item.kind === 'tool_call') group.toolCallCount += 1;

    group.items.push(item);
    if (group.items.length > MAX_EVENTS_PER_GROUP) {
      group.items.splice(0, group.items.length - MAX_EVENTS_PER_GROUP);
    }
  });

  return [...groups.values()];
}
